import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Loader2, Building2, Users, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PENDING_KEY = 'align_pending_invite_token';

/**
 * Invitation acceptance landing page.
 *
 * Three states:
 *   1. Not logged in     → show invite preview + "Sign in to accept" CTA
 *                          Persists token to localStorage so LandingPage can resume after login.
 *   2. Logged in (auth)  → show invite preview + "Accept" button → calls accept endpoint
 *   3. Done/error        → friendly message + next-action link
 */
export default function InvitePage({ token: authToken, setToken: setAuthToken }) {
  const { token: inviteToken } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  // 1. Fetch invitation preview (public — no auth needed).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/invitations/by-token/${inviteToken}`);
        if (!cancelled) setInfo(data);
      } catch (error) {
        if (!cancelled) setLoadError(error.response?.data?.detail || t('invite.loadError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [inviteToken, t]);

  // If user is not logged in, persist the token so LandingPage can resume after login.
  useEffect(() => {
    if (!authToken && inviteToken && info) {
      try { localStorage.setItem(PENDING_KEY, inviteToken); } catch (_) {}
    }
  }, [authToken, inviteToken, info]);

  const handleSignInToAccept = () => {
    // LandingPage reads PENDING_KEY post-login and routes back here.
    navigate('/');
  };

  const handleAccept = async () => {
    if (!authToken) {
      handleSignInToAccept();
      return;
    }
    setAccepting(true);
    try {
      const { data } = await axios.post(
        `${API}/invitations/${inviteToken}/accept`,
        null,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      try { localStorage.removeItem(PENDING_KEY); } catch (_) {}
      // Route by workspace type (corporate vs family). Persist mode + per-mode
      // active workspace so the user lands directly in the right mode with the
      // joined workspace already selected.
      const destMode = info.workspace_type === 'family' ? 'family' : 'corporate';
      try { localStorage.setItem(`align_active_workspace_${destMode}`, data.workspace_id); } catch (_) {}
      try { localStorage.setItem('align_mode', destMode); } catch (_) {}
      setAccepted(true);
      toast.success(t('invite.acceptSuccess', { workspace: info.workspace_name }));
      setTimeout(() => navigate(destMode === 'family' ? '/family' : '/corporate'), 1500);
    } catch (error) {
      const detail = error.response?.data?.detail || t('invite.acceptError');
      toast.error(detail);
      if (error.response?.status === 401) {
        setAuthToken(null);
        handleSignInToAccept();
      }
    } finally {
      setAccepting(false);
    }
  };

  // ============ Render ============

  if (loading) {
    return (
      <FullScreenCard>
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </FullScreenCard>
    );
  }

  if (loadError) {
    return (
      <FullScreenCard>
        <div className="inline-flex w-14 h-14 rounded-full bg-destructive/10 items-center justify-center mb-4">
          <X className="w-7 h-7 text-destructive" />
        </div>
        <h1 className="font-serif text-2xl font-light mb-2">{t('invite.errorTitle')}</h1>
        <p className="text-sm text-muted-foreground mb-6 max-w-sm">{loadError}</p>
        <Button onClick={() => navigate(authToken ? '/dashboard' : '/')} variant="outline" className="rounded-full">
          {authToken ? t('invite.backToDashboard') : t('invite.backToHome')}
        </Button>
      </FullScreenCard>
    );
  }

  if (accepted) {
    return (
      <FullScreenCard>
        <div className="inline-flex w-14 h-14 rounded-full bg-accent/15 items-center justify-center mb-4">
          <Check className="w-7 h-7 text-accent" />
        </div>
        <h1 className="font-serif text-2xl font-light mb-2">{t('invite.welcomeTitle', { workspace: info.workspace_name })}</h1>
        <p className="text-sm text-muted-foreground">{t('invite.redirecting')}</p>
      </FullScreenCard>
    );
  }

  const expired = info.status === 'expired' || (info.expires_at && new Date(info.expires_at) < new Date());
  const used = info.status === 'accepted';

  if (expired || used) {
    return (
      <FullScreenCard>
        <div className="inline-flex w-14 h-14 rounded-full bg-secondary items-center justify-center mb-4">
          <X className="w-7 h-7 text-muted-foreground" />
        </div>
        <h1 className="font-serif text-2xl font-light mb-2">
          {used ? t('invite.alreadyUsedTitle') : t('invite.expiredTitle')}
        </h1>
        <p className="text-sm text-muted-foreground mb-6 max-w-sm">
          {used ? t('invite.alreadyUsedBody') : t('invite.expiredBody')}
        </p>
        <Button onClick={() => navigate(authToken ? '/dashboard' : '/')} variant="outline" className="rounded-full">
          {authToken ? t('invite.backToDashboard') : t('invite.backToHome')}
        </Button>
      </FullScreenCard>
    );
  }

  return (
    <FullScreenCard>
      <div className="inline-flex w-14 h-14 rounded-full bg-accent/15 items-center justify-center mb-4">
        {info.workspace_type === 'family' ? <Users className="w-7 h-7 text-accent" /> : <Building2 className="w-7 h-7 text-accent" />}
      </div>
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">{t('invite.label')}</p>
      <h1 className="font-serif text-2xl sm:text-3xl font-light mb-2 leading-tight">
        {t('invite.heading', { workspace: info.workspace_name })}
      </h1>
      <p className="text-sm text-muted-foreground mb-6 max-w-sm">
        {t('invite.subheading', { role: t(`members.role.${info.role}`), workspace: info.workspace_name })}
      </p>
      {authToken ? (
        <Button onClick={handleAccept} disabled={accepting} size="lg" className="rounded-full" data-testid="invite-accept-btn">
          {accepting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
          {t('invite.acceptCta')}
        </Button>
      ) : (
        <Button onClick={handleSignInToAccept} size="lg" className="rounded-full" data-testid="invite-signin-btn">
          {t('invite.signInToAccept')}
        </Button>
      )}
    </FullScreenCard>
  );
}

function FullScreenCard({ children }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12" data-testid="invite-page">
      <div className="max-w-md w-full text-center bg-card/40 border border-border rounded-2xl p-8 sm:p-10 fade-in">
        {children}
      </div>
    </div>
  );
}

export { PENDING_KEY };
